<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    width="1000"
  >
    <v-card>
      <v-card-title primary-title>
        Upload a photos
        <div class="text-center">
          <v-dialog
            v-model="photoRequirementsDialog"
            width="500"
          >
            <template #activator="{ on, attrs }">
              <v-btn
                class="ml-2"
                small
                text
                v-bind="attrs"
                v-on="on"
                @click="photoRequirementsDialog = true"
              >
                (See requirements)
              </v-btn>
            </template>

            <v-card>
              <v-card-title class="text-h6 grey lighten-2">
                Passport photo requirements
              </v-card-title>

              <v-card-text>
                <ul>
                  <li>high resolution photo that is not blurry, grainy, or pixelated.</li>
                  <li>2 x 2 inches (51 x 51 mm)</li>
                  <li>Head must be between 1 -1 3/8 inches (25 - 35 mm) from the bottom of the chin to the top of the head</li>
                  <li>Printed on matte or glossy photo quality paper</li>
                  <p>
                    We suggest you to use <a
                      href="https://tsg.phototool.state.gov/photo"
                      target="_blank"
                    >this tool</a> to make sure your photo is passport compliant.
                  </p>
                </ul>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="primary"
                  text
                  @click="photoRequirementsDialog = false"
                >
                  Dismiss
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <v-row
              v-if="hadPhoto && currentUser.atleastContributor"
              justify="center"
              class="align-center"
            >
              <v-col cols="auto">
                <p>Current photo</p>
                <v-img
                  class="photo-preview rounded-lg"
                  contain
                  height="400"
                  width="400"
                  :src="peGetPhotoDataUrl"
                />
              </v-col>
              <v-col cols="auto">
                <v-btn
                  color="primary"
                  @click="_ => onDownloadPhoto(peParty.photo_original)"
                >
                  Download Original Photo
                </v-btn>
              </v-col>
              <v-col cols="auto">
                <v-menu offset-y>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      dark
                      v-bind="attrs"
                      v-on="on"
                    >
                      Export As
                      <v-icon right>
                        mdi-export
                      </v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="_ => onPrintPhoto()">
                      <v-list-item-title>Print-Ready PDF (2 pictures)</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="_ => onDownloadPhoto(peParty.photo)">
                      <v-list-item-title>
                        Single JPG
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-col>
              <v-col v-if="!editing">
                <v-btn
                  color="primary"
                  @click="_ => setEditingMode(true)"
                >
                  Edit photo
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row v-if="editing">
          <v-col>
            <v-row>
              <v-col>
                <p class="font-weight-bold text-overline">
                  Please select photo
                </p>
                <v-file-input
                  v-model="selectedPhoto"
                  label="Photo Select"
                  filled
                  accept="image/*"
                  prepend-icon="mdi-camera"
                  @change="_ => onFileChange()"
                />
              </v-col>
            </v-row>

            <v-row
              v-if="(hadPhoto || selectedPhoto) && currentUser.atleastContributor"
            >
              <v-col cols="6">
                <span class="text-overline text-center font-weight-bold d-block">Original Photo</span>
                <Cropper
                  ref="cropper"
                  class="cropper"
                  :src="image.src"
                  :stencil-props="{
                    aspectRatio: 1
                  }"
                  @change="event => onCropperChange(event)"
                />
              </v-col>
              <v-col cols="6">
                <v-row>
                  <v-col>
                    <span class="text-overline text-center font-weight-bold d-block">Passport Photo Preview</span>
                    <div class="preview-wrapper">
                      <v-img
                        class="photo-preview"
                        contain
                        height="400"
                        width="400"
                        :src="resultImg"
                      />
                      <v-img
                        class="preview-guide"
                        height="400"
                        width="400"
                        src="/images/passport-photo-guide.svg"
                      />
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn
          v-if="hadPhoto"
          color="error"
          text
          @click="onFileDelete"
        >
          Delete
        </v-btn>
        <v-spacer />
        <v-btn
          color="secondary"
          text
          @click="onDialogClose"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          text
          :disabled="!selectedPhoto"
          @click="_ => onFileUpload()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import 'vue-advanced-cropper/dist/style.css'
import { mapActions, mapGetters } from 'vuex'
import { Cropper } from 'vue-advanced-cropper'

import { downloadURI, getMimeType } from '@/utilities'
import { alertHandler } from '@/alertHandler'
import { parseErrorResponseMessage } from '@/utilities/error-responses'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

export default {
  name: 'PEPartyPhotoDialog',

  components: {
    Cropper,
  },

  props: {
    value: {
      type: Boolean,
      required: true,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      editing: false,
      hadPhoto: false,

      image: {
        src: null,
        type: null,
      },

      photoRequirementsDialog: false,
      resultImgLocal: '',
      selectedPhoto: null,
    }
  },

  computed: {
    dialog: {
      get () { return this.value },

      set (val) {
        this.$emit('input', val)
      },
    },

    resultImg: {
      get () { return this.selectedPhoto ? this.resultImgLocal : this.image?.src },
      set (val) { this.resultImgLocal = val },
    },

    ...mapGetters([
      'currentUser',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'peGetPhotoDataUrl',
      'peGetPhotoOriginalBlob',
      'peGetPhotoOriginalDataUrl',
      'peParty',
    ]),
  },

  watch: {
    value (val) {
      if (val) {
        this.checkIfHasPhoto()
      }
    },
  },

  destroyed () {
    this.clearCache()
  },

  methods: {
    checkIfHasPhoto () {
      this.clearCache()
      if (this.peGetPhotoOriginalDataUrl) {
        this.image.src = this.peGetPhotoOriginalDataUrl
        this.selectedPhoto = new File([this.peGetPhotoOriginalBlob], 'photo_original.jpg')
        this.hadPhoto = true
      } else {
        this.hadPhoto = false
        this.setEditingMode(true)
      }
    },

    clearCache () {
      this.setEditingMode(false)
      this.resultImgLocal = ''
      this.selectedPhoto = null
      this.image.src = null
    },

    onCropperChange ({ canvas }) {
      this.resultImg = canvas.toDataURL()
    },

    onDialogClose () {
      this.clearCache()
      this.dialog = false
    },

    async onDownloadPhoto ({ blob, id }) {
      this.displayAlert('success', 'Generating photo link to download...')
      this.downloadAttachment(id).
        then(fullResPhoto => {
          downloadURI(URL.createObjectURL(fullResPhoto), blob.filename)
        }).
        catch(e => this.displayAlert('error', parseErrorResponseMessage(e)))
    },

    onFileChange () {
      if (!this.selectedPhoto) {
        this.clearCache()
        return
      }

      if (this.image.src) {
        URL.revokeObjectURL(this.image.src)
      }

      const blob = URL.createObjectURL(this.selectedPhoto)
      const reader = new FileReader()

      reader.onload = e => {
        // Note: arrow function used here, so that "this.image" refers to the image of Vue component
        this.image = {
          // Set the image source (it will look like blob:http://example.com/2c5270a5-18b5-406e-a4fb-07427f5e7b94)
          src: blob,
          // Determine the image type to preserve it during the extracting the image from canvas:
          type: getMimeType(e.target.result, this.selectedPhoto.type),
        }
      }

      reader.readAsArrayBuffer(this.selectedPhoto)
    },

    onFileDelete () {
      this.deleteAttachment(this.peParty?.photo?.id).
        then(_ => {
          this.displayAlert('success', 'Party Photo Delete Successfully')
          this.peRefreshPhoto()
          this.onDialogClose()
        })
    },

    onFileUpload () {
      const { canvas } = this.$refs.cropper.getResult()
      canvas.toBlob(blob => {
        this.peUploadPartyPhoto({
          filename: this.selectedPhoto.name,
          photo: blob,
          photoOriginal: this.selectedPhoto,
        }).
          then(_ => {
            this.displayAlert('success', 'Party Photo Uploaded Successfully')
            this.dialog = false
          }).
          catch(e => this.displayAlert('error', parseErrorResponseMessage(e)))
      }, this.image.type)
    },

    async onPrintPhoto () {
      this.printPhoto(this.peParty.id)
    },

    setEditingMode (value) {
      this.editing = value
    },

    ...mapActions(PARTY_NAMESPACE, [
      'peRefreshPhoto',
      'peUploadPartyPhoto',
      'printPhoto',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'deleteAttachment',
      'downloadAttachment',
    ]),
  },
}
</script>

<style scoped lang="scss">
.preview-wrapper {
  position: relative;
  width: 400px;
  height:400px;
  margin: auto;

  .preview-guide, .photo-preview {
    position: absolute;
    top: 0;
    left: 0;
  }

  .preview-guide {
    z-index: 666;
  }
}
</style>
